import {sum, isEmpty, mapValues} from 'lodash';
import {groupToGoogleItem} from "./googleAnalyticsItems";

/**
 * Used only on the OrderComplete page, this builds the "purchase" event for GA4.
 *
 * @param eventType
 * @param order
 * @param orderedItems
 * @param groupCollection
 * @returns {Array}
 */
export function buildGA4OrderEvent({
  eventType = 'purchase',
  order = {},
  orderedItems = [],
  groupCollection = {}
}) {
  if (isEmpty(order))
    return null;

  return [
    'event',
    eventType,
    buildOrderEcommerceObject({order, orderedItems, groupCollection})
  ];
}

export function buildGTMOrderEvent({
  eventType = 'purchase',
  order = {},
  orderedItems = [],
  groupCollection = {}
}) {
  if (isEmpty(order))
    return null;

  return {
    event: eventType,
    ecommerce: buildOrderEcommerceObject({order, orderedItems, groupCollection})
  }
}

// chatbot tells me to make these always numeric
function centsToDollars(cents) {
  if (!cents) return 0;

  let usd = new Money(cents).toDollars();
  return +usd;
}

/*
 * For Google Tag Manager this is the 'ecommerce' member of the event.
 * For Legacy GA4 this is the 3rd param of the event array.
 * The contents are identical.
 */
export function buildOrderEcommerceObject({order, orderedItems = [], groupCollection}) {
  let googleItems = orderedItems.map((orderedItem, index) =>
    orderedItemToGoogleItem({orderedItem, order, index, groupCollection}));

  return {
    transaction_id: ""+order.id,
    // [Google example comment] Sum of (price * quantity) for all items.
    value: centsToDollars(order.subtotalCents),
    tax: centsToDollars(order.taxCents),
    shipping: centsToDollars(order.shippingCents),
    currency: 'USD',
    coupon: order.couponCode || '',
    // customer_type: notused - this is an arbitrary value if we want,
    items: googleItems
  }
}

export function orderedItemToGoogleItem(
  {
    orderedItem, order, index, groupCollection = {}, productGroup
  }) {
  const {catalogPriceEachCents = 0, actualPriceEachCents = 0, productGroupId} = orderedItem;
  const discountCents = catalogPriceEachCents - actualPriceEachCents;

  productGroup ||= groupCollection["" + productGroupId] || groupCollection[+productGroupId];

  let groupAttrs = groupToGoogleItem(productGroup);
  // item_id, item_name, item_brand, item_variant, price, quantity, item_category[n]

  let itemAttrs = {
    // Mike S 2026-03, for GTM/GA4 - this was already available as google_sku in props:
    // FOR NOLA - Change to AAG-group_id-hex(item_id) or AAG-3267-13790DB4
    // FOR AAG - Change to group_id-vendorsku or 3267-622203

    item_id: orderedItem.googleSku,
    item_variant: `${orderedItem.productGroupId}-${orderedItem.vendorsku}`,
    quantity: orderedItem.quantity,
    index: index,
    item_variant_name: orderedItem.name,
    price: centsToDollars(actualPriceEachCents),
    discount: centsToDollars(discountCents > 0 ? discountCents : 0),
    coupon: (discountCents > 0) ? order?.couponCode : null,
    vehicle_make: orderedItem.vehicleMakeName,
    vehicle_model: orderedItem.vehicleModelName,
    vehicle_year: orderedItem.vehicleYear,
    vehicle_type: orderedItem.vehicleType
  };

  // now merge in the group attrs, with itemAttrs taking priority
  itemAttrs = Object.assign({}, groupAttrs, itemAttrs);

  return mapValues(itemAttrs,
    // convert nulls to empty strings
    x => (x === null) ? '' : x
  );
}
